<template>
  <div>
    <ak-field label="Folder" class="mb-tiny" name="folders">
      <ak-select
        v-model="computedValue"
        :options="folders"
        name="folders"
        placeholder="Select a folder"
        :max-height="240"
      />
    </ak-field>
    <ak-field>
      <ak-button
        size="tiny"
        @click.prevent="createFolderModal = !createFolderModal"
      >
        Create Folder
      </ak-button>
    </ak-field>
    <ak-create-folder-modal
      v-model:is-active="createFolderModal"
      :folders-list="folders"
      :folder-created="folderCreated"
    />
  </div>
</template>
<script>
import { ref, computed, defineComponent, onMounted } from 'vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import CreateFolderModal from './CreateFolderModal.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { request } from '@akerolabs/akero-vue/src/utils/request'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-folder-form-select />
   */
  name: 'AkFolderFormSelect',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [CreateFolderModal.name]: CreateFolderModal,
    [Field.name]: Field,
    [Button.name]: Button,
    [Select.name]: Select,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Number|String}
     * @description The value of the select input
     */
    modelValue: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },
  },

  emits: ['update:modelValue'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const createFolderModal = ref(false)
    const folders = ref([])

    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @returns {Promise}
     * @description Fetches the folders from the API
     */
    const fetchFolders = async () => {
      const { data: res } = await request.get(
        `/accounts/${props.activeAccount._id}`,
        {
          params: {
            agency: props.activeAgency._id,
            overrideAccess: true,
            filters: 'settings.workflow.folders',
            per_page: 0,
          },
        },
      )

      folders.value = res.data.settings.workflow.folders
    }

    /**
     * @param {Srring} folderName
     * @description Pushes a new folder into the folders array and save
     * the folders back into the account via the API.
     */
    const folderCreated = async (folderName) => {
      folders.value.push(folderName)
      await saveFolders()
      computedValue.value = folderName
      createFolderModal.value = false
    }

    /**
     * @returns {Promise}
     * @description Saves the folders in the users account
     */
    const saveFolders = async () => {
      return request.put(
        `/accounts/${props.activeAccount._id}/setting/folders`,
        { 'settings.workflow.folders': folders.value },
      )
    }

    onMounted(async () => {
      try {
        await fetchFolders()
      } catch (ex) {
        ToastProgrammatic.open({
          message: 'Error failed to fetch workflow folders.',
          type: 'danger',
        })
      }
    })

    return {
      createFolderModal,
      computedValue,
      folders,
      fetchFolders,
      folderCreated,
    }
  },
})
</script>
